import React, { useState } from "react"

import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import CloseIcon from "@mui/icons-material/Close"
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  useMediaQuery,
  useTheme as useMuiTheme,
} from "@mui/material"
import { Tab, Tabs, Typography } from "@synapse-analytics/synapse-ui"

import { Labels } from "../../features/Workflows"
import { getTheme } from "../../hooks/UseTheme"
import type { RootState } from "../../store/ReduxStore"
import { Deployment } from "../../types/generated/api/Deployment"
import { useIsPermitted } from "../../utils/PermissionsHelpers"
import { ProjectConstantsTable } from "../tables/ProjectConstantsTable"
import { ProjectSchemaTable } from "../tables/ProjectSchemaTable"

/**
 * Props for the ProjectConfigurationDialog component
 */
type Props = {
  /** Controls the visibility of the dialog */
  isOpen: boolean
  /** Callback function to handle dialog close events */
  onClose: () => void
}

/**
 * ProjectConfigurationDialog component displays a modal dialog for project configuration settings.
 * It includes tabs for Schema and Labels (if user has permissions), allowing users to manage
 * project-specific settings.
 *
 * @component
 * @param {Props} props - Component props
 * @param {boolean} props.isOpen - Controls dialog visibility
 * @param {() => void} props.onClose - Callback function for closing the dialog
 * @returns {React.ReactElement} Rendered dialog component
 */
export function ProjectConfigurationDialog(props: Readonly<Props>): React.ReactElement {
  const { isOpen, onClose } = props
  const { t } = useTranslation("dialogs")

  // Get user permissions from Auth utility
  const permissions = useSelector((state: RootState) => state.auth.permissions)

  // Get theme configurations
  const theme = getTheme()
  const MuiTheme = useMuiTheme()

  // Get flattened permissions from Redux store
  const flattenedKonanPermissions = useSelector((state: RootState) => state.permissions.flattenedKonanPermissions)

  const projectCategory = useSelector((state: RootState) => state.project.currentProject?.category)

  // State to track the active tab
  const [activeTab, setActiveTab] = useState<string>("Schema")

  const tabs: string[] = [
    t("ProjectConfigurationDialog.tabs.schema"),
    ...(projectCategory === Deployment.category.WORKFLOW
      ? ([t("ProjectConfigurationDialog.tabs.constants")] as const)
      : []),
    ...(useIsPermitted("List labels", permissions.konan, flattenedKonanPermissions)
      ? ([t("ProjectConfigurationDialog.tabs.labels")] as const)
      : []),
  ]

  return (
    <Dialog
      maxWidth="md"
      open={isOpen}
      fullWidth
      fullScreen={useMediaQuery(MuiTheme.breakpoints.down("md"))} // Responsive fullscreen on medium and smaller devices
      onClose={onClose}
    >
      {/* Dialog header with title and close button */}
      <DialogTitle className="dialog-header-base" style={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="h2-bold">{t("ProjectConfigurationDialog.title")}</Typography>

        <IconButton onClick={onClose} size="small" className="close-icon-button">
          <CloseIcon style={{ color: theme.palette.base.text[2] }} />
        </IconButton>
      </DialogTitle>

      <DialogContent className="dialog-content-base">
        {/* Tabs navigation */}
        <Grid item xs={12} container mb={1}>
          {/* TODO:: should handle this variant from SUI */}
          <Tabs value={activeTab} className="base-tabs">
            {/* Dynamically generate tabs based on permissions */}
            {tabs.map((tab) => (
              <Tab key={tab} label={tab} onClick={() => setActiveTab(tab)} value={tab} selected={activeTab === tab} />
            ))}
          </Tabs>
        </Grid>

        {/* Tab content */}
        <Grid item xs={12}>
          {activeTab === t("ProjectConfigurationDialog.tabs.schema") && <ProjectSchemaTable />}
          {activeTab === t("ProjectConfigurationDialog.tabs.constants") && <ProjectConstantsTable />}
          {activeTab === t("ProjectConfigurationDialog.tabs.labels") && <Labels />}
        </Grid>
      </DialogContent>
    </Dialog>
  )
}
